/*
 * The mailbox: top bar, folders, the list and the reading pane, compose,
 * settings, and the keyboard. The URL holds where you are:
 *   ?mb=<mailbox>&f=<folder or ~starred>&q=<search>&p=<page>&m=<uid>&mf=<its folder>
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { useBrandName } from "../lib/brand";
import { cx } from "../lib/cx";
import { useTheme } from "../lib/theme";
import { Button } from "../ui/Button";
import { IconKeyboard, IconLogOut, IconMenu, IconMonitor, IconMoon, IconRefresh, IconSearch, IconSettings, IconSun, IconX } from "../ui/icons";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator, MenuTrigger } from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import { useToast } from "../ui/Toast";
import { ApiError, wmApi } from "./api";
import type { ComposeRequest } from "./Compose";
import { describe, keys, STARRED, UNIFIED, useConversation, useFolders, useList, useMailActions, useMessage, useSettings, type ListView } from "./data";
import { Confirm, FolderNameDialog, HelpOverlay, MovePicker, type ConfirmState } from "./dialogs";
import { PickTimeDialog, snoozeChoices } from "./later";
import { ScheduledList, SnoozedList } from "./Waiting";
import { FolderPane } from "./FolderPane";
import { displayName, plural } from "./format";
import { IconArchiveBox, IconDraft, IconFolder, IconInbox, IconInboxes, IconPanes, IconPencil, IconSearchSaved, IconSend, IconSpam, IconStar, IconTrashCan, IconUserPlus } from "./icons";
import { useLiveMail } from "./live";
import { listSubtitle, MessageList, rowKey, type QuickFilter, type RowAction } from "./MessageList";
import { BrandMark, Empty, Kbd, SelfAvatar } from "./parts";
import { useWebmailBrand } from "./brand";
import { ReadingPane } from "./ReadingPane";
import type { SettingsTab } from "./SettingsDrawer";
import type { Account, Addr, Conversation, Folder, HitsResult, Invite, InviteAnswer, ListResult, Message, Prefs, Row, UnifiedResult } from "./types";

// Compose and Settings carry the rich text editor: loaded when first opened.
const Compose = lazy(() => import("./Compose").then((m) => ({ default: m.Compose })));
const SettingsDrawer = lazy(() => import("./SettingsDrawer").then((m) => ({ default: m.SettingsDrawer })));

const OPERATORS = ["from:", "to:", "subject:", "label:", "has:attachment", "is:unread", "is:starred", "before:", "after:", "in:"];

function rowFromConversation(c: Conversation, folder: string, uid: number): Row | null {
  const here = c.messages.filter((m) => m.folder === folder);
  const m = here.find((x) => x.uid === uid) ?? here.at(-1);
  if (!m) return null;
  return {
    folder,
    uid: m.uid,
    uids: here.map((x) => x.uid),
    count: here.length,
    from: m.from,
    to: m.to,
    subject: c.subject,
    snippet: "",
    date: m.date,
    unread: here.some((x) => !x.flags.seen),
    starred: here.some((x) => x.flags.starred),
    answered: m.flags.answered,
    draft: m.flags.draft,
    hasAttachment: m.attachments.length > 0,
  };
}

function isTyping(t: EventTarget | null) {
  const el = t as HTMLElement | null;
  return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
}

export function MailApp({ account, accounts, onAddAccount }: { account: Account; accounts: Account[]; onAddAccount: () => void }) {
  const mb = account.mailboxId;
  useWebmailBrand(account.address); // the header's logo and accent: this mailbox's agency
  const brand = useBrandName();
  const qc = useQueryClient();
  const toast = useToast();
  const theme = useTheme();
  const [params, setParams] = useSearchParams();

  const folder = params.get("f") ?? "INBOX";
  const q = params.get("q");
  const page = Math.max(1, Number(params.get("p")) || 1);
  const openUid = Number(params.get("m")) || null;
  const open = useMemo(() => (openUid ? { folder: params.get("mf") ?? folder, uid: openUid } : null), [openUid, params, folder]);
  // Quick filters (?qf=unread,starred,attachments) narrow a folder through search, so they cover all of it.
  const quick = useMemo(() => new Set((params.get("qf") ?? "").split(",").filter(Boolean) as QuickFilter[]), [params]);

  const folders = useFolders(mb);
  const folderList0 = folders.data?.folders ?? [];
  const filterQuery = useMemo(() => {
    if (!quick.size || q || folder === STARRED || folder === UNIFIED) return null;
    const f = folderList0.find((x) => x.path === folder);
    const ROLE_WORD: Record<string, string> = { inbox: "inbox", sent: "sent", drafts: "drafts", junk: "spam", trash: "trash", archive: "archive" };
    const where = f?.role ? ROLE_WORD[f.role] : `"${(f?.name ?? folder).replace(/"/g, "")}"`;
    return [`in:${where}`, quick.has("unread") ? "is:unread" : null, quick.has("starred") ? "is:starred" : null, quick.has("attachments") ? "has:attachment" : null].filter(Boolean).join(" ");
  }, [quick, q, folder, folderList0]);
  const view: ListView = useMemo(
    () =>
      q
        ? { kind: "search", q }
        : filterQuery
          ? { kind: "search", q: filterQuery }
          : folder === STARRED
            ? { kind: "starred" }
            : folder === UNIFIED
              ? { kind: "unified", page }
              : { kind: "folder", folder, page },
    [q, filterQuery, folder, page],
  );
  const unifiedView = view.kind === "unified";

  const list = useList(mb, view);
  const settings = useSettings(mb);
  const prefs = settings.data?.prefs;
  // Where the reading pane goes (right, below or off): the mailbox's own preference, so every browser agrees.
  const pane: "right" | "below" | "off" = prefs?.readingPane ?? "right";
  /** Saves preferences (just what changed); says why when the API refuses. */
  const savePrefsNow = async (patch: Partial<Prefs>): Promise<string | null> => {
    if (!prefs) return "Settings are still loading.";
    try {
      const r = await wmApi.patch<{ settings: NonNullable<typeof settings.data> }>(`/m/${mb}/settings`, { prefs: patch });
      qc.setQueryData(keys.settings(mb), r.settings);
      return null;
    } catch (err) {
      return err instanceof ApiError ? (Object.values(err.fields)[0] ?? err.message) : "That wasn't saved. Try again.";
    }
  };
  const savePrefs = (patch: Partial<Prefs>) => {
    if (!prefs) return;
    qc.setQueryData(keys.settings(mb), (old: typeof settings.data) => (old ? { ...old, prefs: { ...old.prefs, ...patch } } : old));
    void savePrefsNow(patch).then((e) => e && toast({ tone: "error", title: e }));
  };
  const setPane = (v: "right" | "below" | "off") => savePrefs({ readingPane: v });
  const labelList = prefs?.labels ?? [];
  const labelName = useCallback((id: string) => labelList.find((l) => l.id === id)?.name ?? null, [labelList]);
  const dragRows = useRef<Row[]>([]);
  const lastToggled = useRef<number | null>(null);
  const actions = useMailActions(mb);
  const [imagesFor, setImagesFor] = useState<string | null>(null);
  const openKey = open ? rowKey(open) : null;
  const showImages = !!open && imagesFor === openKey;
  // The clicked message first, the rest of its conversation after.
  const message = useMessage(mb, open, showImages);
  const conv = useConversation(mb, open, showImages, !!message.data);
  const reading = useMemo<Conversation | undefined>(
    () => conv.data ?? (message.data ? { subject: message.data.subject, messages: [message.data] } : undefined),
    [conv.data, message.data],
  );

  const [selected, setSelected] = useState<Map<string, Row>>(new Map());
  const [cursor, setCursor] = useState(0);
  const [keyboardCursor, setKeyboardCursor] = useState(false); // the cursor ring shows once j, k or x are used
  const [compose, setCompose] = useState<ComposeRequest | null>(null);
  const [composeSize, setComposeSize] = useState<"docked" | "min" | "max">("docked");
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);
  const [moving, setMoving] = useState<Row[] | null>(null);
  const [help, setHelp] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [folderDialog, setFolderDialog] = useState<{ mode: "new" } | { mode: "rename"; folder: Folder } | null>(null);
  const [searchText, setSearchText] = useState(q ?? "");
  // "Pick a date and time" for a snooze, and "Save this search".
  const [snoozePick, setSnoozePick] = useState<Row[] | null>(null);
  const [savingSearch, setSavingSearch] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const composeKey = useRef(1);

  useEffect(() => setSearchText(q ?? ""), [q]);
  // Fetch the editor soon after opening, so the first Compose opens at once.
  useEffect(() => {
    const t = setTimeout(() => void import("./Compose"), 1500);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    setSelected(new Map());
    setCursor(0);
    lastToggled.current = null;
  }, [folder, q, page, filterQuery]);

  const folderList = folders.data?.folders ?? [];
  const byPath = useMemo(() => new Map(folderList.map((f) => [f.path, f])), [folderList]);
  const nameOf = useCallback((p: string) => (p === STARRED ? "Starred" : p === UNIFIED ? "All inboxes" : (byPath.get(p)?.name ?? p)), [byPath]);
  const roleOf = (p: string) => byPath.get(p)?.role ?? null;
  const rows = list.data?.rows ?? [];
  // In the unified inbox a row is also its mailbox's: the open message is this mailbox's.
  const openIndex = open ? rows.findIndex((r) => (!r.mailboxId || r.mailboxId === mb) && r.folder === open.folder && (r.uid === open.uid || r.uids.includes(open.uid))) : -1;
  const openRow = openIndex >= 0 ? rows[openIndex]! : open && reading ? rowFromConversation(reading, open.folder, open.uid) : null;
  const inbox = folderList.find((f) => f.role === "inbox");

  /* ── Navigation ── */

  const go = useCallback(
    (next: Record<string, string | null>, replace = false) => {
      const p = new URLSearchParams();
      p.set("mb", mb);
      const merged = { f: params.get("f"), q: params.get("q"), p: params.get("p"), m: params.get("m"), mf: params.get("mf"), qf: params.get("qf"), ...next };
      for (const [k, v] of Object.entries(merged)) if (v) p.set(k, v);
      setParams(p, { replace });
    },
    [mb, params, setParams],
  );
  const goFolder = (path: string) => {
    setDrawer(false);
    go({ f: path === "INBOX" ? null : path, q: null, p: null, m: null, mf: null, qf: null });
  };
  const toggleFilter = (f: QuickFilter) => {
    const next = new Set(quick);
    if (next.has(f)) next.delete(f);
    else next.add(f);
    go({ qf: next.size ? [...next].join(",") : null, p: null, m: null, mf: null });
  };
  const runSearch = (text: string) => {
    const t = text.trim();
    go({ q: t || null, p: null, m: null, mf: null, qf: null });
    setPhoneSearch(false);
    searchRef.current?.blur();
  };
  const closeMessage = () => go({ m: null, mf: null });
  const openCompose = (req: Omit<ComposeRequest, "key">) => {
    if (compose) {
      toast({ tone: "info", title: "Finish or close the message you're writing first." });
      return;
    }
    setCompose({ ...req, key: composeKey.current++ });
  };
  const openRowAt = (r: Row, i: number) => {
    setCursor(i);
    // A unified row from another mailbox: open it there, keeping the unified list.
    if (r.mailboxId && r.mailboxId !== mb) {
      const p = new URLSearchParams({ mb: r.mailboxId, f: UNIFIED, m: String(r.uid), mf: r.folder });
      if (page > 1) p.set("p", String(page));
      setParams(p);
      return;
    }
    if (r.draft && roleOf(r.folder) === "drafts") {
      openCompose({ mode: "draft", source: { folder: r.folder, uid: r.uid } });
      return;
    }
    go({ m: String(r.uid), mf: r.folder === folder ? null : r.folder });
  };

  /* ── Reading marks it read ── */

  // Once the whole conversation is in (or failed): the mark shares the one IMAP connection with it, and
  // shouldn't hold it up. The rows are updated from the server's answer rather than by fetching the list
  // again (which, for a search, would mean searching every folder again).
  const settled = conv.data ?? (conv.isError ? reading : undefined);
  useEffect(() => {
    const c = settled;
    if (!c) return;
    const unread = c.messages.filter((m) => !m.flags.seen);
    if (!unread.length) return;
    const groups = new Map<string, number[]>();
    for (const m of unread) groups.set(m.folder, [...(groups.get(m.folder) ?? []), m.uid]);
    void (async () => {
      const marked = new Map<string, Set<number>>();
      for (const [f, uids] of groups) {
        try {
          await wmApi.post(`/m/${mb}/messages/flags`, { folder: f, uids, add: ["seen"] });
          marked.set(f, new Set(uids));
        } catch {
          /* the list still shows it unread, which is the truth */
        }
      }
      qc.setQueriesData<ListResult | HitsResult>({ queryKey: keys.lists(mb) }, (old) =>
        old ? { ...old, rows: old.rows.map((r) => (r.unread && r.uids.every((u) => !groups.get(r.folder)?.includes(u) || marked.get(r.folder)?.has(u)) && r.uids.some((u) => marked.get(r.folder)?.has(u)) ? { ...r, unread: false } : r)) } : old,
      );
      void qc.invalidateQueries({ queryKey: keys.folders(mb) });
    })();
  }, [settled, mb, qc]);

  /* ── Acting on conversations ── */

  const targets = useCallback((): Row[] => {
    if (selected.size) return [...selected.values()];
    if (openRow) return [openRow];
    const r = rows[cursor];
    return r ? [r] : [];
  }, [selected, openRow, rows, cursor]);

  /** After moving the open conversation away: open the next one down, or go back to the list. */
  const advance = (moved: Row[]) => {
    setSelected(new Map());
    if (!open || !moved.some((r) => openRow && rowKey(r) === rowKey(openRow))) return;
    const next = rows.slice(openIndex + 1).find((r) => !moved.some((m) => rowKey(m) === rowKey(r))) ?? rows.slice(0, Math.max(openIndex, 0)).reverse().find((r) => !moved.some((m) => rowKey(m) === rowKey(r)));
    if (next && pane !== "off" && window.matchMedia("(min-width: 1024px)").matches) go({ m: String(next.uid), mf: next.folder === folder ? null : next.folder }, true);
    else closeMessage();
  };

  const moveTo = (rs: Row[], to: string, say: string) => {
    if (!rs.length) return;
    actions.move.mutate({ rows: rs, to, say }, { onSuccess: () => advance(rs) });
  };
  const archive = () => {
    const rs = targets().filter((r) => roleOf(r.folder) !== "archive");
    if (!rs.length) return toast({ tone: "info", title: "That's already in Archive." });
    moveTo(rs, "archive", `Archived ${describe(rs.length)}.`);
  };
  const trash = () => {
    const rs = targets();
    if (!rs.length) return;
    const forever = rs.every((r) => ["trash", "junk"].includes(roleOf(r.folder) ?? ""));
    if (forever) {
      const n = rs.reduce((a, r) => a + r.uids.length, 0);
      setConfirm({
        title: "Delete forever?",
        body: `${plural(n, "message")} will be deleted for good. This can't be undone.`,
        action: "Delete forever",
        danger: true,
        run: () => actions.destroy.mutateAsync({ rows: rs }).then(() => advance(rs)),
      });
    } else moveTo(rs.filter((r) => roleOf(r.folder) !== "trash"), "trash", `Moved ${describe(rs.length)} to Trash.`);
  };
  const spam = () => {
    const rs = targets();
    if (!rs.length) return;
    if (rs.every((r) => roleOf(r.folder) === "junk")) moveTo(rs, "inbox", `Moved ${describe(rs.length)} to Inbox. It won't be treated as spam.`);
    else moveTo(rs.filter((r) => roleOf(r.folder) !== "junk"), "junk", `Moved ${describe(rs.length)} to Spam.`);
  };
  const setRead = (read: boolean) => {
    const rs = targets();
    if (rs.length) actions.flags.mutate({ rows: rs, ...(read ? { add: ["seen"] } : { remove: ["seen"] }) });
    if (!read && open) closeMessage();
  };
  const star = (rs = targets()) => {
    if (!rs.length) return;
    const on = !rs.every((r) => r.starred);
    actions.flags.mutate({ rows: rs, ...(on ? { add: ["starred"] } : { remove: ["starred"] }) });
  };
  /** Snooze until a time: back to Inbox, unread, then. */
  const snoozeRows = (rs: Row[], until: string) => {
    if (!rs.length) return;
    actions.snooze.mutate({ rows: rs, until }, { onSuccess: () => advance(rs) });
  };
  const snoozeAllowed = (rs: Row[]) => rs.length > 0 && rs.every((r) => !["scheduled", "snoozed", "drafts"].includes(roleOf(r.folder) ?? ""));
  /** Whether the rows (or the open conversation) carry a label: all of them, some, or none. */
  const labelState = (rs: Row[], id: string): "all" | "some" | "none" => {
    const n = rs.filter((r) => r.labels?.includes(id)).length;
    return n === 0 ? "none" : n === rs.length ? "all" : "some";
  };
  const toggleLabel = (rs: Row[], id: string, on: boolean) => {
    if (!rs.length) return;
    actions.label.mutate({ rows: rs, ...(on ? { add: [id] } : { remove: [id] }) });
  };
  const answerInvite = async (m: Message, answer: InviteAnswer): Promise<Invite> => {
    const r = await wmApi.post<{ invite: Invite }>(`/m/${mb}/invite/reply`, { folder: m.folder, uid: m.uid, answer });
    void qc.invalidateQueries({ queryKey: keys.conversations(mb) });
    toast({ tone: "success", title: answer === "accepted" ? "Accepted. Your answer was sent." : answer === "tentative" ? "Answered maybe. Your answer was sent." : "Declined. Your answer was sent." });
    return r.invite;
  };

  const startMove = () => {
    const rs = targets();
    if (rs.length) setMoving(rs);
  };
  const reply = (m: Message | null, mode: "reply" | "replyAll" | "forward") => {
    const target = m ?? reading?.messages.at(-1) ?? null;
    if (!target) return;
    openCompose({ mode, source: { folder: target.folder, uid: target.uid } });
  };
  const block = (sender: Addr) => {
    const rs = targets();
    setConfirm({
      title: `Block ${sender.address}?`,
      body: "Their mail goes straight to Spam. You can unblock them in Settings.",
      action: "Block",
      danger: true,
      run: async () => {
        try {
          const s = settings.data ?? (await wmApi.get<{ settings: { blocked: string[] } }>(`/m/${mb}/settings`)).settings;
          await wmApi.patch(`/m/${mb}/settings`, { blocked: [...new Set([...s.blocked, sender.address])] });
          void qc.invalidateQueries({ queryKey: keys.settings(mb) });
          moveTo(rs.filter((r) => roleOf(r.folder) !== "junk"), "junk", `Blocked ${sender.address} and moved this to Spam.`);
        } catch (err) {
          toast({ tone: "error", title: err instanceof ApiError ? err.message : "That sender wasn't blocked." });
        }
      },
    });
  };

  /* ── Live mail ── */

  const live = useLiveMail(mb, (fresh) => {
    if (!prefs?.notifications || typeof Notification === "undefined" || Notification.permission !== "granted" || !document.hidden) return;
    for (const m of fresh.slice(-3)) {
      const n = new Notification(displayName(m.from), { body: m.subject || "(no subject)", tag: `${mb}:${m.uid}` });
      n.onclick = () => {
        window.focus();
        go({ f: null, q: null, p: null, m: String(m.uid), mf: null });
        n.close();
      };
    }
  });

  // The tab says how much unread mail is waiting.
  useEffect(() => {
    const unread = inbox?.unseen ?? 0;
    const where = q ? `Search` : nameOf(folder);
    document.title = `${unread ? `(${unread.toLocaleString("en-US")}) ` : ""}${where} · ${account.address}`;
  }, [inbox?.unseen, q, folder, nameOf, account.address]);

  /* ── Keyboard ── */

  const keyState = useRef<{ g: number }>({ g: 0 });
  const handlers = useRef<(e: KeyboardEvent) => void>(() => {});
  handlers.current = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    if (isTyping(e.target)) return;
    if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return;
    const k = e.key;
    if (keyState.current.g && Date.now() - keyState.current.g < 1200) {
      keyState.current.g = 0;
      if (k === "i") return goFolder("INBOX");
      if (k === "s") return goFolder(STARRED);
    }
    const n = rows.length;
    switch (k) {
      case "g":
        keyState.current.g = Date.now();
        return;
      case "/":
        e.preventDefault();
        if (window.matchMedia("(max-width: 767px)").matches) setPhoneSearch(true);
        setTimeout(() => searchRef.current?.focus(), 0);
        return;
      case "c":
        e.preventDefault();
        return openCompose({ mode: "new" });
      case "?":
        return setHelp(true);
      case "j":
      case "k": {
        if (!n) return;
        setKeyboardCursor(true);
        const from = open && openIndex >= 0 ? openIndex : cursor;
        const to = Math.min(n - 1, Math.max(0, from + (k === "j" ? 1 : -1)));
        setCursor(to);
        if (open) openRowAt(rows[to]!, to);
        return;
      }
      case "o":
      case "Enter":
        if (!open && rows[cursor]) openRowAt(rows[cursor]!, cursor);
        return;
      case "u":
      case "Escape":
        if (open) closeMessage();
        else if (k === "Escape" && selected.size) setSelected(new Map());
        return;
      case "x": {
        setKeyboardCursor(true);
        const r = rows[cursor];
        if (r) toggleSelect(r);
        return;
      }
      case "e":
        return archive();
      case "#":
        return trash();
      case "!":
        return spam();
      case "s":
        return star();
      case "I":
        return setRead(true);
      case "U":
        return setRead(false);
      case "v":
        e.preventDefault();
        return startMove();
      case "r":
        return reply(null, prefs?.replyMode === "replyAll" ? "replyAll" : "reply");
      case "a":
        return reply(null, "replyAll");
      case "f":
        return reply(null, "forward");
    }
  };
  useEffect(() => {
    const h = (e: KeyboardEvent) => handlers.current(e);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  function toggleSelect(r: Row, index = rows.findIndex((x) => rowKey(x) === rowKey(r)), range = false) {
    const from = lastToggled.current;
    lastToggled.current = index;
    setSelected((s) => {
      const n = new Map(s);
      if (range && from !== null && from !== index) {
        // Shift-click: everything between the last one toggled and this one takes this one's new state.
        const on = !n.has(rowKey(r));
        for (const x of rows.slice(Math.min(from, index), Math.max(from, index) + 1)) {
          if (on) n.set(rowKey(x), x);
          else n.delete(rowKey(x));
        }
        return n;
      }
      if (n.has(rowKey(r))) n.delete(rowKey(r));
      else n.set(rowKey(r), r);
      return n;
    });
  }

  /** A row's own hover action: on that row alone, whatever else is selected. */
  const rowAction = (r: Row, a: RowAction) => {
    if (a === "archive") {
      if (roleOf(r.folder) === "archive") return toast({ tone: "info", title: "That's already in Archive." });
      return moveTo([r], "archive", "Archived 1 conversation.");
    }
    if (a === "trash") {
      if (["trash", "junk"].includes(roleOf(r.folder) ?? "")) return setConfirm({ title: "Delete forever?", body: `${plural(r.uids.length, "message")} will be deleted for good. This can't be undone.`, action: "Delete forever", danger: true, run: () => actions.destroy.mutateAsync({ rows: [r] }).then(() => advance([r])) });
      return moveTo([r], "trash", "Moved 1 conversation to Trash.");
    }
    if (a === "star") return star([r]);
    if (a === "mute" || a === "unmute") return actions.mute.mutate({ rows: [r], muted: a === "mute" });
    actions.flags.mutate({ rows: [r], ...(a === "read" ? { add: ["seen"] } : { remove: ["seen"] }) });
  };
  /** Rows dropped on a folder in the folder pane. */
  const dropOn = (f: Folder) => {
    const rs = dragRows.current.filter((r) => r.folder !== f.path);
    dragRows.current = [];
    if (!rs.length) return;
    moveTo(rs, f.role === "archive" ? "archive" : f.role === "trash" ? "trash" : f.role === "junk" ? "junk" : f.role === "inbox" ? "inbox" : f.path, `Moved ${describe(rs.length)} to ${f.name}.`);
  };

  /* ── The list's words ── */

  const listData = list.data as (ListResult & HitsResult & Partial<UnifiedResult>) | undefined;
  const filtering = !!filterQuery;
  const title = q ? "Search" : nameOf(folder);
  const subtitle = filtering ? `${plural(listData?.total ?? 0, "match", "matches")}${listData?.truncated ? ", newest 100 shown" : ""}` : listSubtitle(view.kind, listData);
  // The unified inbox: mailboxes that couldn't be read are said, and the rest still show.
  const unifiedProblems = unifiedView ? (listData?.mailboxes ?? []).filter((m) => m.problem) : [];
  const accountOf = (id: string) => accounts.find((a) => a.mailboxId === id);
  const showRecipients = view.kind === "folder" && ["sent", "drafts"].includes(roleOf(folder) ?? "");
  // Empty places teach: what lands here, and the one thing that helps.
  const role = roleOf(folder);
  const empty = filtering ? (
    <Empty icon={<IconSearch size={20} />} title="Nothing matches these filters" action={<button type="button" className="text-[13px] font-medium text-link hover:underline" onClick={() => go({ qf: null })}>Show everything in {nameOf(folder)}</button>}>
      Turn a filter off, or search every folder with <Kbd>/</Kbd>.
    </Empty>
  ) : view.kind === "search" ? (
    <Empty icon={<IconSearch size={20} />} title={`Nothing matches ${view.q}`}>
      Try fewer words, or narrow it with from:, subject:, has:attachment or in:archive.
    </Empty>
  ) : unifiedView ? (
    <Empty icon={<IconInboxes size={20} />} title="Every inbox is clear">
      Mail from all your mailboxes, together.
    </Empty>
  ) : view.kind === "starred" ? (
    <Empty icon={<IconStar size={20} />} title="No starred messages">
      Star a conversation with <Kbd>s</Kbd> to keep it here.
    </Empty>
  ) : role === "inbox" ? (
    <Empty icon={<IconInbox size={20} />} title="You're all caught up" action={<Button size="sm" variant="secondary" leading={<IconPencil size={14} />} onClick={() => openCompose({ mode: "new" })}>Write a message</Button>}>
      All caught up.
    </Empty>
  ) : role === "drafts" ? (
    <Empty icon={<IconDraft size={20} />} title="No drafts">
      No drafts.
    </Empty>
  ) : role === "sent" ? (
    <Empty icon={<IconSend size={20} />} title="Nothing sent yet">
      Nothing sent yet.
    </Empty>
  ) : role === "archive" ? (
    <Empty icon={<IconArchiveBox size={20} />} title="Nothing archived yet">
      Archived mail stays searchable.
    </Empty>
  ) : role === "junk" ? (
    <Empty icon={<IconSpam size={20} />} title="No spam">
      No spam.
    </Empty>
  ) : role === "trash" ? (
    <Empty icon={<IconTrashCan size={20} />} title="Trash is empty">
      Trash is empty.
    </Empty>
  ) : (
    <Empty icon={<IconFolder size={20} />} title={`Nothing in ${nameOf(folder)}`}>
      Move conversations here with <Kbd>v</Kbd>.
    </Empty>
  );
  const pager =
    unifiedView && listData && (listData.next || listData.page > 1)
      ? {
          from: (listData.page - 1) * listData.pageSize + 1,
          to: Math.min(listData.page * listData.pageSize, listData.total),
          total: listData.total,
          prev: listData.page > 1 ? () => go({ p: listData.page - 1 > 1 ? String(listData.page - 1) : null, m: null, mf: null }) : null,
          next: listData.next ? () => go({ p: String(listData.next), m: null, mf: null }) : null,
        }
      : view.kind === "folder" && listData && listData.total > listData.pageSize
      ? {
          from: (listData.page - 1) * listData.pageSize + 1,
          to: Math.min(listData.page * listData.pageSize, listData.total),
          total: listData.total,
          prev: listData.page > 1 ? () => go({ p: listData.page - 1 > 1 ? String(listData.page - 1) : null, m: null, mf: null }) : null,
          next: listData.page * listData.pageSize < listData.total ? () => go({ p: String(listData.page + 1), m: null, mf: null }) : null,
        }
      : null;
  const notice = unifiedProblems.length
    ? unifiedProblems.map((m) => `${m.address ?? accountOf(m.mailboxId)?.address ?? "A mailbox"} isn't in this list right now: ${m.problem}`).join(" ")
    : view.kind === "starred" && listData && listData.total > listData.rows.length
      ? `Showing the newest ${listData.rows.length} of ${listData.total.toLocaleString("en-US")} starred messages.`
      : view.kind === "search" && listData?.truncated
        ? `Showing the newest ${listData.rows.length}. Add words or operators to narrow it down.`
        : null;
  const listRole = q ? null : roleOf(folder);
  const single = pane === "off";
  const below = pane === "below";
  const fetching = useIsFetching({ queryKey: keys.all(mb) }) > 0;

  const emptyText = !open ? (rows.length ? "Choose a conversation to read it here. j and k move through the list." : list.isPending ? "" : "Nothing to read here.") : null;

  return (
    <div className="flex h-dvh flex-col bg-canvas">
      {/* ── Top bar ── */}
      <header className="relative flex h-14 shrink-0 items-center justify-between gap-2 border-b border-line bg-surface px-3 sm:px-4">
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" onClick={() => setDrawer(true)} className="-ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md text-body hover:bg-hover lg:hidden" aria-label="Folders">
            <IconMenu size={20} />
          </button>
          <button type="button" onClick={() => goFolder("INBOX")} aria-label="Inbox" className="rounded-md">
            <BrandMark />
          </button>
        </div>

        <div className={cx("max-w-[672px] flex-1 px-2 md:px-4", phoneSearch ? "absolute inset-x-0 top-0 z-30 flex h-14 items-center bg-surface px-3" : "hidden md:block")}>
          <form
            className="relative w-full"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch(searchText);
            }}
          >
            <IconSearch size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-faint" />
            <input
              ref={searchRef}
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setPhoneSearch(false);
                  searchRef.current?.blur();
                }
              }}
              placeholder="Search mail"
              aria-label="Search every folder"
              className="h-9 w-full rounded-md bg-panel pr-12 pl-9 text-sm text-fg outline-none transition-colors placeholder:text-faint hover:bg-hover focus:bg-surface focus:ring-1 focus:ring-brand-500 [&::-webkit-search-cancel-button]:hidden"
            />
            {q || phoneSearch ? (
              <button
                type="button"
                onClick={() => {
                  setSearchText("");
                  setPhoneSearch(false);
                  if (q) go({ q: null, m: null, mf: null });
                }}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-faint hover:text-fg"
                aria-label="Clear search"
              >
                <IconX size={14} />
              </button>
            ) : (
              <span className="absolute top-1/2 right-3 -translate-y-1/2">
                <Kbd>/</Kbd>
              </span>
            )}
            {searchFocused && !searchText ? (
              <div className="pop absolute top-full right-0 left-0 z-40 mt-1.5 p-3">
                <p className="mb-2 text-[11px] text-dim">Searches every folder. Narrow it down with:</p>
                <div className="flex flex-wrap gap-1.5">
                  {OPERATORS.map((op) => (
                    <button
                      key={op}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSearchText(op.endsWith(":") ? op : `${op} `);
                        searchRef.current?.focus();
                      }}
                      className="rounded bg-panel px-1.5 py-0.5 font-mono text-[11.5px] text-body ring-1 ring-line hover:text-fg"
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </form>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={() => {
              setPhoneSearch(true);
              setTimeout(() => searchRef.current?.focus(), 0);
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-body hover:bg-hover md:hidden"
            aria-label="Search"
          >
            <IconSearch size={18} />
          </button>
          {/* The one clear Compose (c): the panel's primary button. On a phone it's the round button at the bottom. */}
          <span className="hidden sm:block">
            <Button variant="primary" leading={<IconPencil size={15} />} onClick={() => openCompose({ mode: "new" })}>
              Compose
            </Button>
          </span>
          <Tooltip content="Refresh">
            <button type="button" onClick={() => void actions.refresh()} className="hidden h-9 w-9 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg sm:inline-flex" aria-label="Refresh">
              <IconRefresh size={16} className={fetching ? "animate-spin" : undefined} />
            </button>
          </Tooltip>
          <Menu>
            <Tooltip content="Layout">
              <MenuTrigger className="hidden h-9 w-9 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-brand-500 lg:inline-flex" aria-label="Layout">
                <IconPanes size={17} />
              </MenuTrigger>
            </Tooltip>
            <MenuContent align="end" className="w-56">
              <MenuLabel>Reading pane</MenuLabel>
              <MenuRadioGroup value={pane} onValueChange={(v) => setPane(v as "right" | "below" | "off")}>
                <MenuRadioItem value="right">To the right</MenuRadioItem>
                <MenuRadioItem value="below">Below the list</MenuRadioItem>
                <MenuRadioItem value="off">Off: open on its own</MenuRadioItem>
              </MenuRadioGroup>
              <MenuSeparator />
              <MenuLabel>Density</MenuLabel>
              <MenuRadioGroup value={prefs?.density ?? "comfortable"} onValueChange={(v) => savePrefs({ density: v as "comfortable" | "compact" })}>
                <MenuRadioItem value="comfortable">Comfortable</MenuRadioItem>
                <MenuRadioItem value="compact">Compact</MenuRadioItem>
              </MenuRadioGroup>
            </MenuContent>
          </Menu>
          <Menu>
            <MenuTrigger className="rounded-full hover:ring-2 hover:ring-brand-200 focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:outline-none" aria-label="Account menu">
              <SelfAvatar address={account.address} name={account.displayName} url={account.avatarUrl} size={32} />
            </MenuTrigger>
            <MenuContent align="end" className="w-72">
              <div className="flex items-center gap-2.5 px-2.5 py-2">
                <SelfAvatar address={account.address} name={account.displayName} url={account.avatarUrl} size={36} />
                <div className="min-w-0">
                  {account.displayName ? <p className="truncate text-[13px] font-semibold text-fg">{account.displayName}</p> : null}
                  <p className="truncate text-[12.5px] text-dim">{account.address}</p>
                  {account.openedFromPanel ? <p className="mt-0.5 text-[12px] text-dim">Opened from {brand}{account.openedFromPanel.by ? ` by ${account.openedFromPanel.by}` : ""}</p> : null}
                </div>
              </div>
              {accounts.length > 1 ? (
                <>
                  <MenuSeparator />
                  <MenuLabel>Switch mailbox</MenuLabel>
                  {accounts
                    .filter((a) => a.mailboxId !== mb)
                    .map((a) => (
                      <MenuItem key={a.mailboxId} onSelect={() => setParams(new URLSearchParams({ mb: a.mailboxId }))} icon={<SelfAvatar address={a.address} name={a.displayName} url={a.avatarUrl} size={20} />}>
                        <span className="text-[13px]">{a.address}</span>
                      </MenuItem>
                    ))}
                </>
              ) : null}
              <MenuItem icon={<IconUserPlus size={16} />} onSelect={onAddAccount}>
                Add another mailbox
              </MenuItem>
              <MenuSeparator />
              <MenuItem icon={<IconSettings size={16} />} onSelect={() => setSettingsTab("profile")}>
                Settings
              </MenuItem>
              <MenuItem icon={<IconKeyboard size={16} />} shortcut="?" onSelect={() => setHelp(true)}>
                Keyboard shortcuts
              </MenuItem>
              <MenuSeparator />
              <MenuLabel>Theme</MenuLabel>
              <MenuRadioGroup value={theme.pref} onValueChange={(v) => theme.setPref(v as "light" | "dark" | "system")}>
                <MenuRadioItem value="light" icon={<IconSun size={16} />}>
                  Light
                </MenuRadioItem>
                <MenuRadioItem value="dark" icon={<IconMoon size={16} />}>
                  Dark
                </MenuRadioItem>
                <MenuRadioItem value="system" icon={<IconMonitor size={16} />}>
                  Match system
                </MenuRadioItem>
              </MenuRadioGroup>
              <MenuSeparator />
              <MenuItem
                danger
                icon={<IconLogOut size={16} />}
                onSelect={async () => {
                  await wmApi.del(`/session/${mb}`).catch(() => {});
                  qc.removeQueries({ queryKey: keys.all(mb) });
                  await qc.invalidateQueries({ queryKey: ["wm", "session"] });
                  setParams(new URLSearchParams());
                }}
              >
                Sign out of this mailbox
              </MenuItem>
              {accounts.length > 1 ? (
                <MenuItem
                  danger
                  icon={<IconLogOut size={16} />}
                  onSelect={async () => {
                    await wmApi.del(`/session`).catch(() => {});
                    qc.clear();
                    await qc.invalidateQueries({ queryKey: ["wm", "session"] });
                    setParams(new URLSearchParams());
                  }}
                >
                  Sign out of every mailbox
                </MenuItem>
              ) : null}
            </MenuContent>
          </Menu>
        </div>
      </header>

      {live.problem ? (
        <div className="shrink-0 border-b border-line bg-panel px-4 py-1.5 text-center text-[12.5px] text-body" role="status">
          {live.problem}
        </div>
      ) : null}

      {/* ── Three panes ── */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {drawer ? <div className="fixed inset-0 top-14 z-30 bg-[var(--hx-overlay)] lg:hidden" onClick={() => setDrawer(false)} /> : null}
        <aside
          className={cx(
            "w-64 shrink-0 flex-col overflow-hidden border-r border-line bg-surface lg:static lg:flex lg:w-60",
            drawer ? "fixed inset-y-0 top-14 left-0 z-30 flex shadow-[var(--hx-shadow-pop)] lg:shadow-none" : "hidden",
          )}
        >
          <FolderPane
            data={folders.data}
            current={q ? null : folder}
            address={account.address}
            openedBy={account.openedFromPanel ? (account.openedFromPanel.by ?? "") : null}
            onDropTo={dropOn}
            unified={accounts.length > 1 ? { unseen: unifiedView ? (listData?.unseen ?? 0) : ((qc.getQueryData<UnifiedResult>([...keys.unified, 1])?.unseen) ?? 0) } : null}
            saved={prefs?.savedSearches ?? []}
            currentSearch={q}
            onSearch={(text) => {
              setDrawer(false);
              setSearchText(text);
              runSearch(text);
            }}
            onForgetSearch={(x) => savePrefs({ savedSearches: (prefs?.savedSearches ?? []).filter((y) => y.name !== x.name) })}
            onGo={goFolder}
            onNewFolder={() => setFolderDialog({ mode: "new" })}
            onRename={(f) => setFolderDialog({ mode: "rename", folder: f })}
            onDelete={(f) =>
              setConfirm({
                title: `Delete ${f.name}?`,
                body: f.messages ? `Its ${plural(f.messages, "message")} move to Trash first.` : undefined,
                action: "Delete folder",
                danger: true,
                run: async () => {
                  try {
                    const r = await wmApi.del<{ moved: number }>(`/m/${mb}/folders`, { path: f.path });
                    toast({ tone: "success", title: r.moved ? `Deleted ${f.name}. Its ${plural(r.moved, "message")} are in Trash.` : `Deleted ${f.name}.` });
                    if (folder === f.path) goFolder("INBOX");
                  } catch (err) {
                    toast({ tone: "error", title: err instanceof ApiError ? err.message : "The folder wasn't deleted." });
                  } finally {
                    void actions.refresh();
                  }
                },
              })
            }
            onEmpty={(f) =>
              setConfirm({
                title: `Empty ${f.name}?`,
                body: `The ${plural(f.messages, "message")} in ${f.name} will be deleted for good. This can't be undone.`,
                action: `Empty ${f.name}`,
                danger: true,
                run: () => actions.empty.mutateAsync({ path: f.path, name: f.name }).then(() => open && roleOf(open.folder) === f.role && closeMessage()),
              })
            }
          />
        </aside>

        {/* The list and the reading pane: side by side, the pane below the list, or the pane alone once one is open. */}
        <div className={cx("flex min-w-0 flex-1", below ? "lg:flex-col" : "")}>
        <aside
          className={cx(
            "flex-col overflow-hidden border-line bg-surface",
            single
              ? open
                ? "hidden"
                : "flex flex-1"
              : below
                ? cx("w-full lg:h-[44%] lg:shrink-0 lg:border-b", open ? "hidden lg:flex" : "flex")
                : cx("w-full border-r lg:w-[26rem] lg:shrink-0", open ? "hidden lg:flex" : "flex"),
          )}
          aria-label="Conversations"
        >
          {!q && role === "scheduled" ? (
            <ScheduledList mb={mb} openUid={openUid} onOpen={(f, uid) => go({ m: String(uid), mf: f === folder ? null : f })} />
          ) : !q && role === "snoozed" ? (
            <SnoozedList mb={mb} openUid={openUid} onOpen={(f, uid) => go({ m: String(uid), mf: f === folder ? null : f })} />
          ) : (
          <MessageList
            title={title}
            subtitle={subtitle}
            // Labels are each mailbox's own: in the unified inbox, only this mailbox's rows show theirs.
            rows={unifiedView ? rows.map((r) => (r.mailboxId && r.mailboxId !== mb ? { ...r, labels: [] } : r)) : rows}
            loading={list.isFetching}
            error={list.error ? (list.error as Error).message : null}
            onRetry={() => void list.refetch()}
            empty={empty}
            loadingText={q ? "Searching every folder." : null}
            openKey={openRow ? rowKey(openRow) : null}
            cursor={keyboardCursor ? cursor : -1}
            selected={selected}
            showFolder={view.kind !== "folder" && !unifiedView && !filtering}
            folderName={nameOf}
            showRecipients={showRecipients}
            compact={prefs?.density === "compact"}
            grouped={view.kind === "folder" || filtering}
            pager={pager}
            notice={notice}
            filters={q || folder === STARRED || unifiedView ? null : { unread: quick.has("unread"), starred: quick.has("starred"), attachments: quick.has("attachments") }}
            headerAction={
              q && !(prefs?.savedSearches ?? []).some((x) => x.q === q) ? (
                <Button size="sm" variant="ghost" leading={<IconSearchSaved size={14} />} onClick={() => setSavingSearch(true)}>
                  Save this search
                </Button>
              ) : null
            }
            labelName={labelName}
            mailboxOf={
              unifiedView
                ? (r) => {
                    const address = r.address ?? accountOf(r.mailboxId ?? "")?.address ?? "";
                    return address ? { initial: address[0]!, address } : null;
                  }
                : undefined
            }
            snooze={{
              choices: snoozeChoices(),
              onPick: (rs, iso) => {
                const list = rs ?? [...selected.values()];
                if (!snoozeAllowed(list)) return toast({ tone: "info", title: "Mail in Drafts, Scheduled or Snoozed can't be snoozed." });
                snoozeRows(list, iso);
              },
              onCustom: (rs) => setSnoozePick(rs ?? [...selected.values()]),
            }}
            labels={
              unifiedView && [...selected.values()].some((r) => r.mailboxId && r.mailboxId !== mb)
                ? null
                : { list: labelList, has: (id) => labelState([...selected.values()], id), onToggle: (id, on) => toggleLabel([...selected.values()], id, on), onManage: () => setSettingsTab("labels") }
            }
            onFilter={toggleFilter}
            onOpen={(r, i) => {
              setKeyboardCursor(false);
              openRowAt(r, i);
            }}
            onToggleSelect={(r, i, range) => toggleSelect(r, i, range)}
            onSelectAll={(all) => setSelected(all ? new Map(rows.map((r) => [rowKey(r), r])) : new Map())}
            onRowAction={rowAction}
            onDragRows={(r) => {
              dragRows.current = selected.has(rowKey(r)) ? [...selected.values()] : [r];
              return dragRows.current.length;
            }}
            onSwipe={(r, dir) => {
              if (dir === "archive") {
                if (roleOf(r.folder) === "archive") toast({ tone: "info", title: "That's already in Archive." });
                else moveTo([r], "archive", "Archived 1 conversation.");
              } else actions.flags.mutate({ rows: [r], ...(r.unread ? { add: ["seen"] } : { remove: ["seen"] }) });
            }}
            bulk={{
              archive,
              trash,
              spam,
              read: () => setRead(true),
              unread: () => setRead(false),
              move: startMove,
              star: () => star(),
              clear: () => setSelected(new Map()),
              inTrash: listRole === "trash" || listRole === "junk",
              inSpam: listRole === "junk",
              mute: () => {
                const rs = [...selected.values()];
                if (rs.length) actions.mute.mutate({ rows: rs, muted: !rs.every((r) => r.muted) });
              },
              allMuted: selected.size > 0 && [...selected.values()].every((r) => r.muted),
            }}
          />
          )}
        </aside>

        <main
          className={cx(
            "min-h-0 min-w-0 flex-1 overflow-y-auto bg-canvas",
            // Room to scroll the message out from under a docked compose panel.
            compose && (composeSize === "docked" ? "sm:pb-[min(600px,82vh)]" : composeSize === "min" ? "sm:pb-12" : ""),
            single ? (open ? "block" : "hidden") : open ? "block" : "hidden lg:block",
          )}
          aria-label="Reading pane"
        >
          <ReadingPane
            key={openKey ?? "none"}
            mb={mb}
            me={account.address}
            data={reading}
            loading={message.isFetching || conv.isFetching}
            // Looking for the rest: only worth saying when the message is a reply or its row is a conversation.
            restLoading={!conv.data && !conv.error && !!message.data && ((openRow?.count ?? 1) > 1 || !!message.data.inReplyTo || message.data.references.length > 0)}
            restError={conv.error ? (conv.error as Error).message : null}
            error={(message.error as Error | null) ?? null}
            onRetry={() => void (message.error ? message.refetch() : conv.refetch())}
            role={open ? roleOf(open.folder) : null}
            starred={!!openRow?.starred || !!reading?.messages.some((m) => m.flags.starred && m.folder === open?.folder)}
            images={showImages}
            empty={emptyText}
            muted={!!openRow?.muted || !!reading?.messages.some((m) => m.muted && m.folder === open?.folder)}
            labelNames={[...new Set((reading?.messages ?? []).filter((m) => m.folder === open?.folder).flatMap((m) => m.labels ?? []))].map(labelName).filter((x): x is string => !!x)}
            actions={{
              back: closeMessage,
              archive,
              spam,
              trash,
              markUnread: () => setRead(false),
              move: startMove,
              star: () => star(),
              block,
              reply,
              showImages: () => setImagesFor(openKey),
              snooze:
                openRow && snoozeAllowed([openRow])
                  ? { choices: snoozeChoices(), onPick: (iso) => snoozeRows([openRow], iso), onCustom: () => setSnoozePick([openRow]) }
                  : null,
              labels: openRow ? { list: labelList, has: (id) => labelState([{ ...openRow, labels: [...new Set((reading?.messages ?? []).flatMap((m) => m.labels ?? []))] }], id), onToggle: (id, on) => toggleLabel([openRow], id, on), onManage: () => setSettingsTab("labels") } : null,
              answerInvite,
              mute: (on) => openRow && actions.mute.mutate({ rows: [openRow], muted: on }),
              allowSender: async (sender) => {
                try {
                  await wmApi.post(`/m/${mb}/images/allow`, { sender: sender.address });
                  setImagesFor(openKey);
                  void qc.invalidateQueries({ queryKey: keys.settings(mb) });
                  void qc.invalidateQueries({ queryKey: keys.conversations(mb) });
                  toast({ tone: "success", title: `Images from ${sender.address} will always show.` });
                } catch (err) {
                  toast({ tone: "error", title: err instanceof ApiError ? err.message : "That wasn't saved." });
                }
              },
            }}
          />
        </main>
        </div>
      </div>

      {/* Compose on a phone: one round button, out of the way of the list. */}
      {!compose && !open ? (
        <button
          type="button"
          onClick={() => openCompose({ mode: "new" })}
          aria-label="Compose"
          className="fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-[0_10px_24px_-8px_rgba(15,23,42,.45)] sm:hidden"
        >
          <IconPencil size={20} />
        </button>
      ) : null}

      {compose ? (
        <Suspense fallback={null}>
          <Compose
            key={compose.key}
            account={account}
            request={compose}
            signatureHtml={settings.data?.signatureHtml ?? ""}
            identities={settings.data?.identities ?? [account.address]}
            templates={prefs?.templates ?? []}
            onManageTemplates={() => setSettingsTab("templates")}
            onSize={setComposeSize}
            onClose={() => {
              setCompose(null);
              void qc.invalidateQueries({ queryKey: keys.lists(mb) });
              void qc.invalidateQueries({ queryKey: keys.folders(mb) });
            }}
          />
        </Suspense>
      ) : null}

      {settingsTab !== null ? (
        <Suspense fallback={null}>
          <SettingsDrawer open tab={settingsTab} onTab={setSettingsTab} onClose={() => setSettingsTab(null)} account={account} folders={folderList} />
        </Suspense>
      ) : null}
      <MovePicker
        open={!!moving}
        folders={folderList}
        exclude={moving && moving.every((r) => r.folder === moving[0]!.folder) ? moving[0]!.folder : null}
        onClose={() => setMoving(null)}
        onPick={(f) => {
          const rs = moving ?? [];
          setMoving(null);
          moveTo(rs.filter((r) => r.folder !== f.path), f.path, `Moved ${describe(rs.length)} to ${f.name}.`);
        }}
      />
      <HelpOverlay open={help} onClose={() => setHelp(false)} />
      <PickTimeDialog
        open={!!snoozePick}
        title="Snooze until"
        description="It comes back to Inbox, unread."
        action="Snooze"
        onClose={() => setSnoozePick(null)}
        onPick={(iso) => {
          const rs = snoozePick ?? [];
          setSnoozePick(null);
          snoozeRows(rs, iso);
        }}
      />
      <FolderNameDialog
        open={savingSearch}
        title="Save this search"
        initial={(q ?? "").slice(0, 60)}
        action="Save search"
        onClose={() => setSavingSearch(false)}
        onSubmit={async (name) => {
          const e = await savePrefsNow({ savedSearches: [...(prefs?.savedSearches ?? []), { name, q: q ?? "" }] });
          if (!e) toast({ tone: "success", title: `Saved. ${name} is under your folders.` });
          return e;
        }}
      />
      <Confirm state={confirm} onClose={() => setConfirm(null)} />
      <FolderNameDialog
        open={!!folderDialog}
        title={folderDialog?.mode === "rename" ? `Rename ${folderDialog.folder.name}` : "New folder"}
        initial={folderDialog?.mode === "rename" ? folderDialog.folder.name : ""}
        action={folderDialog?.mode === "rename" ? "Rename" : "Create folder"}
        onClose={() => setFolderDialog(null)}
        onSubmit={async (name) => {
          try {
            if (folderDialog?.mode === "rename") {
              const r = await wmApi.patch<{ path: string }>(`/m/${mb}/folders`, { path: folderDialog.folder.path, name });
              if (folder === folderDialog.folder.path) goFolder(r.path);
            } else await wmApi.post(`/m/${mb}/folders`, { name, parent: null });
            void actions.refresh();
            return null;
          } catch (err) {
            return err instanceof ApiError ? (err.fields.name ?? err.message) : "That didn't work. Try again.";
          }
        }}
      />
      {!account.canSend ? (
        <div className="pointer-events-none fixed bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-raised px-3 py-1 text-[12.5px] text-body shadow-[var(--hx-shadow-pop)]">
          Sending is turned off for this mailbox. You can still read mail.
        </div>
      ) : null}
    </div>
  );
}
